import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { LineChart } from '@mui/x-charts-pro/LineChart';
import { PageLayout } from '../../../../PageLayout';
import Text from './../../../../Atoms/Text';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import {
  getAuditChartsSubject,
  getRunTotalAuditsLoadingSubject,
  fetchAuditCharts,
  AuditType,
  RunTotalAuditChartsResponse
} from '../../../../../store/RunTotalAudit';

export const HistoricalCharts = () => {
  const [charts, setCharts] = useState<RunTotalAuditChartsResponse>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const chartsSub = getAuditChartsSubject().subscribe(setCharts);
    const loadingSub = getRunTotalAuditsLoadingSubject().subscribe(setLoading);

    fetchAuditCharts();

    return () => {
      chartsSub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, []);

  const types = Object.values(AuditType);

  const getSeriesConfig = (dataKeyArray: string[]) => {
    return dataKeyArray.map((type) => ({
      type: 'line' as const,
      dataKey: type,
      label: type,
      showMark: true,
      connectNulls: true,
      valueFormatter: (value: number | null) => (value !== null ? `${value.toLocaleString()} rows` : '-')
    }));
  };

  return (
    <PageLayout
      className="w-full"
      dataTestId="historical-charts-page"
      dataCy="historical-charts-card">
      <div className="mb-2 -mt-6">
        <Link
          className="text-indigo inline-block relative"
          title="Back to Run Totals Audit"
          to="/admin/run-total-audits">
          <div className="flex items-center text-small">
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
            &nbsp; Run Totals Audit
          </div>
        </Link>
      </div>
      <div className="mb-6">
        <Text
          color="grey9"
          size="xl"
          weight="medium">
          Historical Charts
        </Text>
      </div>
      {loading || !charts ? (
        <div className="flex justify-center items-center h-[600px]">
          <ComponentSpinner />
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <div className="bg-white p-6 pb-0 rounded-xl border border-grey-3 w-full">
            <Text
              className="text-center mt-3"
              size="md">
              Dataset Size Trend
            </Text>
            <div className="w-full h-[400px]">
              <LineChart
                dataset={charts.afterTotalSeries}
                height={400}
                margin={{ top: 50, bottom: 30, left: 0, right: 40 }}
                series={getSeriesConfig(types)}
                xAxis={[
                  {
                    dataKey: 'date',
                    scaleType: 'point',
                    tickLabelPlacement: 'tick',
                    tickPlacement: 'middle',
                    tickLabelStyle: {
                      fontSize: 10,
                      transform: 'rotate(-90deg)',
                      textAnchor: 'end',
                      translate: '-11px 9px'
                    },
                    height: 100
                  }
                ]}
                yAxis={[
                  {
                    label: 'RECORD COUNT',
                    labelStyle: { fontSize: 12, transform: '', translate: '0px 0px' },
                    tickSize: 0,
                    tickLabelStyle: { fontSize: 9, transform: 'translate(-5px, 0px)' }
                  }
                ]}
                slotProps={{
                  legend: {
                    position: { vertical: 'top', horizontal: 'center' }
                  }
                }}
                sx={{
                  '& .MuiLineElement-root': { strokeWidth: 2.5 }
                }}
              />
            </div>
          </div>
          <div className="bg-white p-6 pb-0 rounded-xl border border-grey-3 w-full">
            <Text
              className="text-center mt-3"
              size="md">
              Row Count Change Trend
            </Text>
            <div className="w-full h-[400px]">
              <LineChart
                dataset={charts.changeSeries}
                height={400}
                margin={{ top: 50, bottom: 30, left: 0, right: 40 }}
                series={getSeriesConfig(types)}
                xAxis={[
                  {
                    dataKey: 'date',
                    scaleType: 'point',
                    tickLabelPlacement: 'tick',
                    tickPlacement: 'middle',
                    tickLabelStyle: {
                      fontSize: 10,
                      transform: 'rotate(-90deg)',
                      textAnchor: 'end',
                      translate: '-11px 9px'
                    },
                    height: 100
                  }
                ]}
                yAxis={[
                  {
                    label: 'RECORD COUNT',
                    labelStyle: { fontSize: 12, transform: '', translate: '0px 0px' },
                    tickSize: 0,
                    tickLabelStyle: { fontSize: 9, transform: 'translate(-5px, 0px)' }
                  }
                ]}
                slotProps={{
                  legend: {
                    position: { vertical: 'top', horizontal: 'center' }
                  }
                }}
                sx={{
                  '& .MuiLineElement-root': { strokeWidth: 2.5 }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};
